import React, { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty } from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";

/**
 * Searchable dropdown. `options` may be strings or {value, label} objects.
 */
export default function Combobox({
  value,
  onChange,
  options,
  placeholder = "Select…",
  emptyText = "Nothing found.",
  testId,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const items = useMemo(
    () => options.map((o) => (typeof o === "string" ? { value: o, label: o } : o)),
    [options]
  );
  const current = items.find((i) => i.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testId}
          type="button"
          role="combobox"
          aria-expanded={open}
          className={`w-full inline-flex items-center justify-between h-10 px-3 rounded-md border text-sm text-left ${className}`}
          style={{ borderColor: "var(--umg-border)", background: "var(--umg-surface)", color: "var(--umg-text)" }}
        >
          <span className={current ? "" : "text-[color:var(--umg-text-2)]"}>
            {current ? current.label : placeholder}
          </span>
          <ChevronsUpDown className="w-4 h-4 opacity-60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-white dark:bg-neutral-900 border border-[color:var(--umg-border)]" align="start">
        <Command>
          <CommandInput placeholder="Search…" data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList className="max-h-64">
            <CommandEmpty>{emptyText}</CommandEmpty>
            {items.map((it) => (
              <CommandItem
                key={it.value}
                value={it.label}
                onSelect={() => { onChange(it.value); setOpen(false); }}
                data-testid={testId ? `${testId}-option-${it.value}` : undefined}
              >
                <Check className={`mr-2 h-4 w-4 ${value === it.value ? "opacity-100" : "opacity-0"}`} />
                {it.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
